import i18n from 'i18next';
import { NewsSendMethod } from 'store/news';
import { Translations } from 'types/Common';
import { ContentLanguage } from 'types/general';
import { SurveyResults } from 'store/surveys';
import { resolveTranslation } from 'utils/general';

const emptyLine = '<div><br></div>';
const header = (text: string) => `<div><strong>${text}</strong></div>`;
const block = (text: string) => `<div>${text}</div>`;

interface SurveyResultsNewsParams {
  language: Uppercase<ContentLanguage>;
  /**
   * Survey title
   */
  title: string;
  /**
   * Survey description
   */
  description: string;
  imageId: string;
  responses: SurveyResults['questions'];
  respondentsCount: number;
}

interface Update {
  name: string;
  sendMethods: NewsSendMethod[];
  translations: Translations<{
    title: string;
    content: string;
    headerImageId: string | null;
    attachmentIds: string[];
  }>;
}

export class SurveyResultsNews implements Update {
  name;
  sendMethods = ['email' as const, 'appNotification' as const];
  translations;

  constructor({ language, ...params }: SurveyResultsNewsParams) {
    const translateWithLangContext = (key: string, values?: object) =>
      i18n.t(key, { ...values, lng: language.toLowerCase() });

    const newsTitle = translateWithLangContext('surveys.resultsNewsTitle', {
      interpolation: { escapeValue: false },
      title: params.title,
    });

    this.name = newsTitle;

    const introductoryBlock = block(
      translateWithLangContext('surveys.resultsNewsIntroductoryBlock'),
    );
    const summaryTitle = translateWithLangContext('benefitExpenses.summary');
    const summaryText = translateWithLangContext(
      'surveys.resultsNewsSummaryText',
      { count: params.respondentsCount },
    );
    const summaryBlock = [header(summaryTitle), block(summaryText)].join(
      emptyLine,
    );
    const responsesTitle = translateWithLangContext(
      'surveys.resultsNewsResponsesSectionTitle',
    );

    const responsesText = params.responses
      .filter((response) => response.type !== 'text_response')
      .map((response, index) => {
        const question =
          response.translations.find(
            (translation) => translation.language === language,
          ) ?? response.translations[0];

        const options = response.options.map((option) => {
          const count = response.optionResponseStats.reduce(
            (total, responseStats) => {
              if (responseStats.optionAnswer.includes(option.index)) {
                total += Number(responseStats.answeredByCount);
              }
              return total;
            },
            0,
          );

          const fraction = response.answeredByCount
            ? count / response.answeredByCount
            : 0;

          return {
            ...option,
            count,
            fraction,
          };
        });

        const questionText = `${index + 1}. ${question.text}`;

        const optionsText = options
          .map((option) => {
            const respondentsCountText = translateWithLangContext(
              'surveys.resultsNewsRespondentsCount',
              { count: option.count },
            );

            const percentageText = new Intl.NumberFormat(undefined, {
              style: 'percent',
            }).format(option.fraction);

            return `<li>${
              resolveTranslation(option.translations, language, language)?.text
            } (${respondentsCountText}, ${percentageText})</li>`;
          })
          .join('');

        return [block(questionText), `<ul>${optionsText}</ul>`].join('');
      })
      .join(emptyLine);

    const responsesBlock = !!responsesText
      ? [header(responsesTitle), responsesText].join(emptyLine)
      : '';

    this.translations = [
      {
        language,
        title: newsTitle,
        content: [introductoryBlock, summaryBlock, responsesBlock]
          .filter((block) => !!block.length)
          .join(emptyLine),
        headerImageId: params.imageId,
        attachmentIds: [],
      },
    ];
  }
}
